import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
} from '@mui/material'
import {
  accountService,
  driverService,
  kycService,
  subscriptionPlanService,
  subscriptionService,
  vehicleService,
  SubscriptionPlanDto,
} from '../api/adminServices'
import AccountOverview from './AccountOverview'
import { normalizePagedData } from '../utils/entityDisplay'

interface AccountDetailDialogProps {
  accountId: string | null
  open: boolean
  onClose: () => void
}

export default function AccountDetailDialog({
  accountId,
  open,
  onClose,
}: AccountDetailDialogProps) {
  const { data: account, isLoading, error } = useQuery({
    queryKey: ['account-detail', accountId],
    queryFn: () => accountService.getById(accountId as string),
    enabled: open && !!accountId,
  })

  const isFreighter = String(account?.type || '').toUpperCase() === 'FREIGHTER'

  const { data: usersData } = useQuery({
    queryKey: ['account-detail-users', accountId],
    queryFn: () => accountService.getUsersByAccount(accountId as string, 0, 100),
    enabled: open && !!accountId,
  })

  const { data: driversData } = useQuery({
    queryKey: ['account-detail-drivers', accountId],
    queryFn: () => driverService.getAllByAccount(accountId as string),
    enabled: open && !!accountId && isFreighter,
  })

  const { data: vehiclesData } = useQuery({
    queryKey: ['account-detail-vehicles', accountId],
    queryFn: () => vehicleService.getAllByAccount(accountId as string),
    enabled: open && !!accountId && isFreighter,
  })

  const { data: subscriptionsPage } = useQuery({
    queryKey: ['subscriptions-for-account-detail'],
    queryFn: () => subscriptionService.getAll(0, 500),
    enabled: open && !!accountId,
    staleTime: 5 * 60 * 1000,
  })

  const { data: subscriptionPlansPage } = useQuery({
    queryKey: ['subscription-plans-for-account-detail'],
    queryFn: () => subscriptionPlanService.getAll(false, 0, 200),
    enabled: open && !!accountId,
    staleTime: 5 * 60 * 1000,
  })

  const { data: kycPendingData } = useQuery({
    queryKey: ['kyc-pending-for-account-detail'],
    queryFn: () => kycService.getPending(0, 200),
    enabled: open && !!accountId,
    staleTime: 5 * 60 * 1000,
  })

  const planById = useMemo(() => {
    const plans = normalizePagedData<SubscriptionPlanDto>(subscriptionPlansPage)
    const map = new Map<string, { name: string; planCode?: string }>()
    plans.forEach((plan) => {
      if (plan?.id) map.set(plan.id, { name: plan.name, planCode: plan.planCode })
    })
    return map
  }, [subscriptionPlansPage])

  const subscriptions = useMemo(() => {
    const all = normalizePagedData<any>(subscriptionsPage)
    if (!accountId) return []
    return all.filter((sub) => sub.accountId === accountId)
  }, [subscriptionsPage, accountId])

  const userCount = useMemo(() => {
    const users = normalizePagedData(usersData)
    if (users.length > 0) return users.length
    const fromAccount = (account as { userCount?: number; usersCount?: number } | undefined)
    return fromAccount?.userCount ?? fromAccount?.usersCount ?? 0
  }, [usersData, account])

  const score = useMemo(() => {
    const a = account as { score?: number; accountScore?: number; rating?: number } | undefined
    return a?.score ?? a?.accountScore ?? a?.rating ?? '—'
  }, [account])

  const vehiclesCount = Array.isArray(vehiclesData) ? vehiclesData.length : 0
  const driversCount = Array.isArray(driversData) ? driversData.length : 0

  const kycPendingCount = useMemo(() => {
    const pending = normalizePagedData<any>(kycPendingData)
    return pending.filter((row) => row.accountId === accountId).length
  }, [kycPendingData, accountId])

  const title =
    account?.businessName || account?.name || (accountId ? `Account ${accountId.substring(0, 8)}…` : 'Account')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {title}
        {account?.type && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
            {account.type}
            {accountId ? ` · ${accountId}` : ''}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2">
            Failed to load account details.
          </Typography>
        )}
        {account && !isLoading && (
          <AccountOverview
            account={account}
            isFreighter={isFreighter}
            userCount={userCount}
            score={score}
            vehiclesCount={vehiclesCount}
            driversCount={driversCount}
            kycPendingCount={kycPendingCount}
            subscriptions={subscriptions}
            planById={planById}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
